import { apiClient } from './client';
import type {
  EntityAttachment,
  CreateEntityAttachmentInput,
  UpdateEntityAttachmentInput,
  BulkMoveEntityAttachmentsInput,
  EntityAttachmentUploadUrlResponse,
} from '@peles/shared';

export async function listEntityAttachments(
  entityType: string,
  entityId: string,
  folderId?: string | null,
): Promise<EntityAttachment[]> {
  const params: Record<string, string> = { entityType, entityId };
  if (folderId !== undefined && folderId !== null) params.folderId = folderId;
  const { data } = await apiClient.get<EntityAttachment[]>('/entity-attachments', { params });
  return data;
}

/**
 * Batch-fetch attachments for many entities of the same type in one request.
 * Returns a flat list; each attachment carries its own `entityId` for grouping.
 */
export async function listEntityAttachmentsForEntities(
  entityType: string,
  entityIds: string[],
): Promise<EntityAttachment[]> {
  if (entityIds.length === 0) return [];
  const { data } = await apiClient.get<EntityAttachment[]>('/entity-attachments', {
    params: { entityType, entityIds: entityIds.join(',') },
  });
  return data;
}

export async function listEntityAttachmentsByFolder(folderId: string): Promise<EntityAttachment[]> {
  const { data } = await apiClient.get<EntityAttachment[]>('/entity-attachments', {
    params: { folderId },
  });
  return data;
}

export async function getAttachmentUploadUrl(
  fileName: string,
  mimeType: string,
): Promise<EntityAttachmentUploadUrlResponse> {
  const { data } = await apiClient.post<EntityAttachmentUploadUrlResponse>(
    '/entity-attachments/upload-url',
    { fileName, mimeType },
  );
  return data;
}

export async function createEntityAttachment(
  input: CreateEntityAttachmentInput,
): Promise<EntityAttachment> {
  const { data } = await apiClient.post<EntityAttachment>('/entity-attachments', input);
  return data;
}

export async function updateEntityAttachment(
  id: string,
  input: UpdateEntityAttachmentInput,
): Promise<EntityAttachment> {
  const { data } = await apiClient.patch<EntityAttachment>(`/entity-attachments/${id}`, input);
  return data;
}

export async function deleteEntityAttachment(id: string): Promise<void> {
  await apiClient.delete(`/entity-attachments/${id}`);
}

export async function bulkMoveEntityAttachments(
  input: BulkMoveEntityAttachmentsInput,
): Promise<{ moved: number }> {
  const { data } = await apiClient.post<{ moved: number }>('/entity-attachments/bulk-move', input);
  return data;
}
